// DevHub UI kit — backend data hook.
//
// Replaces the ~14 hand-rolled `{ data, loading, error }` fetch triples across the
// app with one hook. It resolves the backend base URL from config and issues the
// request through Backstage's authenticated fetch (fetchApiRef) — the same
// pattern McpTokenManager and the admin dashboards (UsageAnalyticsPage) use:
//   const { fetch } = useApi(fetchApiRef);
//   const backendUrl = config.getString('backend.baseUrl');
//   await fetch(`${backendUrl}${path}`)
//
// fetchApiRef attaches the caller's Backstage identity token automatically, so
// guarded backend routes authenticate without any per-caller token wiring.
//
// A cancel guard drops stale responses when the component unmounts or `path`
// changes before the request resolves.
import { useEffect, useState } from 'react';
import { configApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';

export interface UseBackendDataResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

export function useBackendData<T>(path: string): UseBackendDataResult<T> {
  const config = useApi(configApiRef);
  const { fetch: authFetch } = useApi(fetchApiRef);
  const backendUrl = config.getString('backend.baseUrl');

  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Cancel guard — drop a stale response if the component unmounts (or `path`
    // changes) before the fetch resolves, so it can't setState late.
    let ignore = false;
    setLoading(true);
    setError(null);
    authFetch(`${backendUrl}${path}`)
      .then(async resp => {
        if (!resp.ok) {
          throw new Error(`Request failed (${resp.status})`);
        }
        const json = (await resp.json()) as T;
        if (!ignore) {
          setData(json);
          setLoading(false);
        }
      })
      .catch(err => {
        if (!ignore) {
          setError(err?.message || String(err));
          setLoading(false);
        }
      });
    return () => {
      ignore = true;
    };
  }, [path, backendUrl, authFetch]);

  return { data, loading, error };
}
